import { Button, TextArea } from '@lego/connect-components-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface MessageInputProps {
  onSend: (text: string) => void;
  disabled: boolean;
}

export const MessageInput = ({ onSend, disabled }: MessageInputProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Move focus into the dialog on open. The Connect overlay does not auto-focus its
  // content, so without this a keyboard user stays on the launcher and cannot Esc out.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  // Enter submits; Shift+Enter inserts a newline.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-200">
      <TextArea
        ref={inputRef}
        aria-label={t('assistant.display.inputPlaceholder')}
        placeholder={t('assistant.display.inputPlaceholder')}
        value={value}
        onChange={(_event, next) => setValue(next)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        autoGrow
        maxRows={5}
        className="flex-1"
      />
      <Button type="submit" variant="primary" disabled={disabled || value.trim().length === 0}>
        {t('assistant.action.send')}
      </Button>
    </form>
  );
};
